import React from 'react';

import { classnames } from '@lumapps/classnames';
import { useDispatch } from '@lumapps/redux/react';
import { useScrollToHash } from '@lumapps/utils/hooks/useScrollToHash';
import { getCustomClasses } from '@lumapps/utils/string/getCustomClasses';
import { actions as widgetActions } from '@lumapps/widget-base/ducks/slice';

import { LayoutProps } from '../../types';
import { renderTree } from './renderTree';

import './index.scss';

/**
 * Render layout (row > cell > widget)
 */
export const Layout: React.FC<LayoutProps> = (props) => {
    const { properties, id, components, contentRef, widgetOverrides } = props;
    const className = classnames('layout', getCustomClasses(properties?.class, 'app-content--module-'), {
        /**
         * On the first render of the page, since layout needs to be fetched and there is no previous layout rendered,
         * we need to set a fix height for the page container in order to avoid showing the footer and then hiding it,
         * once the layout response has arrived. In order to do that, we check that there is no layout id. If that happens,
         * it means that this is the first layout that we display, hence the first page we will render. while navigating
         * through the application we won't have this problem since we will retrieve layout before we display the page.
         */
        'layout--is-loading': Boolean(!id),
    });

    /**
     * The following code handle the scroll to a component using the hash of the URL.
     * We wait a little bit for the DOM to load before trying to scroll.
     * We handle the timeout here instead of inside the hook because we only want to delay the hook at first render,
     *  not when triggered on click (for example using the Comment button of a Metadata widget).
     */
    const [readyToScroll, setReadyToScroll] = React.useState(false);
    /* istanbul ignore next */
    setTimeout(() => {
        setReadyToScroll(true);
    }, 500);
    useScrollToHash(Boolean(id) && readyToScroll);

    const dispatch = useDispatch();

    React.useEffect(() => {
        // On unmount => reset widgets
        return () => {
            dispatch(widgetActions.reset());
        };
    }, [dispatch]);

    return (
        <div ref={contentRef} id={properties?.id} className={className}>
            {components ? renderTree(components, widgetOverrides) : undefined}
        </div>
    );
};

export default Layout;

export type { LayoutProps };
